/**
 * Angebots-Detail.
 *
 * Zweispaltig: links Status, Beteiligte, Mail-Verlauf, Gesprächsnotizen und
 * Statusverlauf – rechts der Datensatz und die weiteren Angebote des Kunden.
 *
 * Zwei Warnbanner sind bewusst dauerhaft, nicht flüchtig:
 *  - fehlgeschlagener Sync zu Monday/SharePoint
 *  - in Monday pausiert, aber ohne Wiedervorlage-Datum
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { IconGesendet, IconMonday, IconTelefon } from '../komponenten/Icons';
import { AuftragModal } from '../modals/AuftragModal';
import { VersendetModal } from '../modals/VersendetModal';
import { GewerkModal } from '../modals/GewerkModal';
import { AnrufModal } from '../modals/AnrufModal';
import { MailVorschauModal } from '../modals/MailVorschauModal';
import { betragFormatieren, betragLesen, dat, eur, gruppiere } from '../hilfen/format';
import type { Angebot, AngebotAntwort } from '../typen';
import './detail.css';

const AKTIVE_STATUS = ['in Arbeit', 'Gesendet', 'Rückfrage', 'Freigabe Technik'];

export function AngebotDetail() {
  const { id } = useParams();
  const angebotId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const meta = useMeta();

  const [auftragOffen, setAuftragOffen] = useState(false);
  const [versendetOffen, setVersendetOffen] = useState(false);
  const [gewerkOffen, setGewerkOffen] = useState(false);
  const [anrufOffen, setAnrufOffen] = useState(false);
  const [mailOffen, setMailOffen] = useState(false);
  const [auftragFeld, setAuftragFeld] = useState<string | null>(null);

  const abfrage = useQuery({
    queryKey: ['angebot', angebotId],
    queryFn: () => api.hole<Angebot>(`/angebote/${angebotId}`),
    enabled: Number.isFinite(angebotId),
  });

  const alleAbfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
  });

  function aktualisieren() {
    void queryClient.invalidateQueries({ queryKey: ['angebot', angebotId] });
    void queryClient.invalidateQueries({ queryKey: ['angebote'] });
  }

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      api.sende<AngebotAntwort>(`/angebote/${angebotId}/status`, { status }),
    onSuccess: (a) => {
      aktualisieren();
      if (a.sync.fehler) toastZeigen('Status gespeichert – der Abgleich meldet ein Problem.');
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => api.sende<AngebotAntwort>(`/angebote/${angebotId}/sync`),
    onSuccess: (a) => {
      aktualisieren();
      if (a.toast) toastZeigen(a.toast);
    },
  });

  const wertMutation = useMutation({
    mutationFn: (auftrag: number | null) =>
      api.aendere<Angebot>(`/angebote/${angebotId}/auftragswert`, { auftrag }),
    onSuccess: aktualisieren,
  });

  const abgerechnetMutation = useMutation({
    mutationFn: (abgerechnet: boolean) =>
      api.aendere<Angebot>(`/angebote/${angebotId}/abgerechnet`, { abgerechnet }),
    onSuccess: aktualisieren,
  });

  const versenderMutation = useMutation({
    mutationFn: (versender: string) =>
      api.aendere<Angebot>(`/angebote/${angebotId}/versender`, { versender }),
    onSuccess: aktualisieren,
  });

  const a = abfrage.data;

  const geschwister = useMemo(
    () => (alleAbfrage.data ?? []).filter((x) => a && x.kunde.id === a.kunde.id && x.id !== a.id),
    [alleAbfrage.data, a],
  );

  const verlauf = useMemo(() => {
    if (!a) return [];
    const absteigend = [...a.verlauf].reverse();
    return absteigend.map((h, i) => {
      const bis = i === 0 ? meta.heute : absteigend[i - 1]!.datum;
      const dauer = Math.round(
        (new Date(bis + 'T00:00:00').getTime() - new Date(h.datum + 'T00:00:00').getTime()) /
          86_400_000,
      );
      return {
        ...h,
        dauerText:
          dauer <= 0
            ? 'heute'
            : `${dauer} ${dauer === 1 ? 'Tag' : 'Tage'} ${i === 0 ? 'offen' : 'gedauert'}`,
        linie: i < absteigend.length - 1,
        aktuell: i === 0,
      };
    });
  }, [a, meta.heute]);

  if (abfrage.isLoading) {
    return <div className="eh-inhalt">Angebot wird geladen …</div>;
  }
  if (!a) {
    return (
      <div className="eh-inhalt">
        <div className="eh-karte">Dieses Angebot gibt es nicht (mehr).</div>
      </div>
    );
  }

  // Solange das Angebot noch nicht beim Kunden liegt, fehlt der Startschuss
  // für die Nachfass-Uhr – dann ist "Angebot versendet" die wichtigste Aktion.
  const kannVersendet = a.status === 'in Arbeit' || a.status === 'Freigabe Technik';
  const kannAnruf = [...AKTIVE_STATUS, 'Pausiert'].includes(a.status);
  const kannErteilen = a.status !== 'Auftrag erteilt' && a.status !== 'Abgesagt';

  const erinnBadge = !AKTIVE_STATUS.includes(a.status)
    ? `Kein Nachfassen – Status ${a.status}`
    : a.nachfass.antwortAm
      ? `Kundenantwort am ${dat(a.nachfass.antwortAm)} erkannt – kein Anruf nötig`
      : !a.nachfass.aktiv
        ? 'Nachfassen pausiert'
        : a.nachfass.mailVerfallen
          ? `Erinnerung entfällt – seit ${dat(a.nachfass.anrufAb)} ist der Anruf dran`
          : a.nachfass.erledigt < 1
            ? `1. Erinnerung geht automatisch am ${dat(a.nachfass.mailAm)} raus`
            : a.nachfass.anrufFaellig
              ? `Anruf ausstehend seit ${dat(a.nachfass.anrufAb)}`
              : `Anruf fällig am ${dat(a.nachfass.anrufAb)}`;

  const erinnTon = a.nachfass.antwortAm
    ? 'gruen'
    : a.nachfass.anrufFaellig
      ? 'rot'
      : 'neutral';

  const versQuelle = !a.versender
    ? 'Versender · im Monday-Log nicht gefunden'
    : a.versManuell
      ? 'Versender · manuell nachgetragen'
      : 'Versender · automatisch aus Monday übernommen';

  const versHinweis = a.versender
    ? (a.versManuell
        ? 'Manuell nachgetragen – Name und Signatur unter den Erinnerungsmails kommen ab jetzt von dieser Person.'
        : 'Name und Signatur unter den Erinnerungsmails kommen vom Versender – ermittelt aus dem Monday-Activity-Log (wer den Status auf „Gesendet" gesetzt hat).') +
      (a.versAbweichend ? ` Hier abweichend vom Kalkulanten ${a.kalkulant}.` : '')
    : `Der Versender konnte nicht aus Monday ermittelt werden – vermutlich vor Einführung dieser Funktion angelegt. Bis zur Nachtrage senden die Erinnerungen im Namen des Kalkulanten ${a.kalkulant}.`;

  const erinnLog = a.erinnerungen
    .map(
      (e) =>
        `1. Erinnerung ${e.art === 'auto' ? 'automatisch versendet' : e.artText} am ${e.datumText} im Namen von ${e.user}` +
        (e.trockenlauf ? ' (Testbetrieb: nur als Entwurf abgelegt)' : ''),
    )
    .concat(
      a.nachfass.antwortAm
        ? [`Kundenantwort am ${dat(a.nachfass.antwortAm)} im Outlook-Postfach erkannt`]
        : [],
    )
    .join('  ·  ');

  const wertOffen = a.auftrag === null || a.auftrag === 0;
  const wertWarnung = a.status === 'Auftrag erteilt' && wertOffen;

  const auftragHinweis = wertWarnung
    ? 'Status ist Auftrag erteilt – bitte Auftragshöhe eintragen, der Wert fließt in die Gewinnprognose ein.'
    : a.status === 'Auftrag erteilt'
      ? a.abgerechnet
        ? 'Abgerechnet – zählt nicht mehr in die Prognose, sondern in die Ist-Zahlen.'
        : 'Auftrag erteilt und noch nicht abgerechnet – Wert zählt in die Gewinnprognose.'
      : 'Wird bei Statuswechsel auf Auftrag erteilt zur Pflichtangabe. Netto, vor Steuern.';

  const datensatz = [
    { label: 'Laufende Nummer', wert: String(a.nr) },
    { label: 'Kategorie', wert: `${a.prefix} – ${a.kategorie}` },
    { label: 'Kalkulant', wert: a.kalkulant },
    { label: 'Kunde', wert: a.kunde.name },
    { label: 'Ort', wert: a.kunde.ort },
    { label: 'E-Mail', wert: a.kunde.mail || '—' },
    { label: 'Statuswechsel', wert: String(a.verlauf.length) },
    { label: 'Auftragshöhe', wert: eur(a.auftrag) },
  ];

  return (
    <div className="detail">
      <button type="button" className="detail-zurueck" onClick={() => navigate(-1)}>
        ← Zurück
      </button>

      <div className="detail-kopf">
        <div>
          <div className="detail-kategorie">
            <span className="detail-prefix">{a.prefix}</span>
            <span className="detail-kategorie-text">
              {a.kategorie} · angelegt {dat(a.angelegtAm)}
            </span>
          </div>
          <div className="detail-name">{a.name}</div>
          <div className="detail-kunde">
            {a.kunde.name} · {a.kunde.adresse}, {a.kunde.ort} · {a.kunde.telefon || '—'}
          </div>
        </div>

        <div className="detail-verknuepfungen">
          <VerknuepfungsKarte
            label="Monday-Item"
            text="Kommunikation & Verlauf ↗"
            url={a.mondayUrl}
            leerHinweis="Noch nicht verknüpft"
          />
          <VerknuepfungsKarte
            label="SharePoint"
            text="Angebotsordner ↗"
            url={a.sharepointUrl}
            leerHinweis="Noch nicht verknüpft"
          />
        </div>
      </div>

      <div className="detail-raster">
        <div className="detail-spalte">
          {/* ---- Statuskarte ---- */}
          <div className="detail-karte">
            <div className="detail-status-kopf">
              <div>
                <div className="detail-label">Aktueller Status</div>
                <div className="detail-status-zeile">
                  <span className="detail-status-punkt" style={{ background: a.statusPunkt }} />
                  <span
                    className="detail-status-text"
                    style={{
                      color: a.status === 'Auftrag erteilt' ? 'var(--eh-gruen)' : 'var(--eh-text)',
                    }}
                  >
                    {a.status}
                  </span>
                  <span
                    className="detail-alter"
                    style={{ background: a.ampel.bg, color: a.ampel.fg }}
                  >
                    {a.alterText} im Status
                  </span>
                </div>

                {(a.pausiert || a.wvFaellig) && (
                  <div className="detail-pause">
                    <span className="detail-pause-punkt" />
                    <span>
                      {a.pausiert
                        ? `Pausiert bis ${dat(a.wiedervorlage)}`
                        : `Wiedervorlage fällig seit ${dat(a.wiedervorlage)}`}
                    </span>
                  </div>
                )}

                {a.status === 'Auftrag erteilt' && a.auftrag ? (
                  <div className="detail-auftragswert">
                    <span className="detail-auftragswert-label">Auftragswert</span>
                    <span className="detail-auftragswert-zahl">{a.auftragText}</span>
                    <span className="detail-auftragswert-note">netto, vor Steuern</span>
                  </div>
                ) : null}
              </div>

              <div className="detail-aktionen">
                <div className="detail-aktionen-knoepfe">
                  {kannVersendet && (
                    <button
                      type="button"
                      className="detail-knopf-versendet"
                      onClick={() => setVersendetOffen(true)}
                    >
                      <IconGesendet />
                      Angebot versendet
                    </button>
                  )}
                  {kannAnruf && (
                    <button
                      type="button"
                      className="detail-knopf-anruf"
                      onClick={() => setAnrufOffen(true)}
                    >
                      <IconTelefon />
                      Anruf dokumentieren
                    </button>
                  )}
                  {/* Nur an einem Fensterangebot und nur, solange BE oder IS
                      noch fehlen – das entscheidet der Server über
                      `ergaenzbareGewerke`, nicht diese Ansicht. */}
                  {a.ergaenzbareGewerke.length > 0 && (
                    <button
                      type="button"
                      className="detail-knopf-gewerk"
                      onClick={() => setGewerkOffen(true)}
                      title="Beschattung oder Insektenschutz unter derselben Nummer ergänzen"
                    >
                      <span style={{ fontSize: 15, lineHeight: 1 }}>+</span>Gewerk ergänzen
                    </button>
                  )}
                  {kannErteilen && (
                    <button
                      type="button"
                      className="detail-knopf-auftrag"
                      onClick={() => setAuftragOffen(true)}
                    >
                      <span style={{ fontSize: 14, lineHeight: 1 }}>✓</span>Auftrag erteilt
                    </button>
                  )}
                </div>
                <div className="detail-seit">
                  seit {a.statusDatumText}
                  <br />
                  durch {a.statusUser}
                </div>
              </div>
            </div>

            {a.wvKonflikt && (
              <div className="detail-warnung">
                <div className="detail-warnung-text">
                  <span className="detail-warnung-icon">!</span>
                  <div>
                    <div className="detail-warnung-titel">
                      In Monday pausiert, aber kein Wiedervorlage-Datum hinterlegt – bitte nachtragen
                    </div>
                    <div className="detail-warnung-unter">
                      Ohne Datum taucht das Angebot nie wieder von selbst auf.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="detail-warnung-knopf"
                  onClick={() => setAnrufOffen(true)}
                >
                  Anruf nachtragen
                </button>
              </div>
            )}

            {a.syncFehler && (
              <div className="detail-warnung">
                <div className="detail-warnung-text">
                  <span className="detail-warnung-icon">!</span>
                  <div>
                    <div className="detail-warnung-titel">
                      Status lokal gesetzt, Sync zu Monday/SharePoint fehlgeschlagen
                    </div>
                    <div className="detail-warnung-unter">{a.syncFehler}</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="detail-warnung-knopf"
                  disabled={syncMutation.isPending}
                  onClick={() => syncMutation.mutate()}
                >
                  {syncMutation.isPending ? 'Läuft …' : 'Erneut versuchen'}
                </button>
              </div>
            )}

            {/* ---- Beteiligte ---- */}
            <div className="detail-beteiligte">
              <div className="detail-personen">
                <div className="detail-person">
                  <span className="eh-avatar detail-avatar" title="Profilbild aus Monday">
                    {a.kalkInitialen}
                  </span>
                  <span className="detail-person-text">
                    <span className="detail-person-name">{a.kalkulant}</span>
                    <span className="detail-person-rolle">Kalkulant · fachlich verantwortlich</span>
                  </span>
                </div>

                <div className="detail-person">
                  <span
                    className="eh-avatar detail-avatar"
                    title="Versender der Erinnerungsmails"
                    style={
                      a.versender
                        ? a.versAbweichend
                          ? { background: 'var(--eh-rot-flaeche)', color: 'var(--eh-rot)' }
                          : undefined
                        : { background: 'var(--eh-gelb-flaeche)', color: 'var(--eh-gelb-versender)' }
                    }
                  >
                    {a.versInitialen}
                  </span>
                  <span className="detail-person-text">
                    <span
                      className="detail-person-name"
                      style={{ color: a.versender ? 'var(--eh-text)' : 'var(--eh-gelb-versender)' }}
                    >
                      {a.versender || 'Nicht ermittelt'}
                    </span>
                    <span className="detail-person-rolle detail-person-quelle">
                      {a.versender && !a.versManuell && <IconMonday groesse={11} />}
                      {versQuelle}
                    </span>
                  </span>
                </div>

                {!a.versender && (
                  <div className="detail-vers-nachtrag">
                    <select
                      className="detail-vers-auswahl"
                      value=""
                      onChange={(e) => versenderMutation.mutate(e.target.value)}
                    >
                      <option value="">Nicht ermittelt</option>
                      {meta.team
                        .filter((p) => p.imVertrieb)
                        .map((p) => (
                          <option key={p.name} value={p.name}>
                            {p.name}
                            {p.name === a.kalkulant ? ' (Kalkulant)' : ''}
                          </option>
                        ))}
                    </select>
                    <span className="detail-vers-hinweis">manuell nachtragen</span>
                  </div>
                )}
              </div>

              <div>
                <span className={`detail-erinn-badge ${erinnTon}`}>{erinnBadge}</span>
              </div>
            </div>

            <div className="detail-vers-text">{versHinweis}</div>
            {erinnLog && <div className="detail-vers-text">{erinnLog}</div>}

            {/* ---- Statuswechsel ---- */}
            <div className="detail-abschnitt">
              <div className="detail-abschnitt-titel">Status ändern</div>
              <div className="eh-chips">
                {meta.status
                  .filter((s) => s !== 'Pausiert')
                  .map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`detail-status-chip ${s === a.status ? 'aktiv' : ''}`}
                      disabled={statusMutation.isPending}
                      onClick={() => statusMutation.mutate(s)}
                    >
                      <span
                        className="detail-chip-punkt"
                        style={{ background: meta.statusPunkt[s] ?? '#A89C92' }}
                      />
                      {s}
                    </button>
                  ))}
              </div>
              <div className="detail-status-hinweis">
                Pausieren geht nur über „Anruf dokumentieren" – dort wird das Wiedervorlage-Datum
                erfasst.
              </div>
            </div>

            {/* ---- Auftragshöhe ---- */}
            <div className="detail-abschnitt">
              <div className={`detail-auftragsfeld ${wertWarnung ? 'warnung' : ''}`}>
                <div className="detail-auftragsfeld-zeile">
                  <label className="detail-auftrag-label">
                    <span>Auftragshöhe · netto, vor Steuern</span>
                    <span className="detail-auftrag-eingabe">
                      <input
                        value={
                          auftragFeld ?? (a.auftrag ? gruppiere(a.auftrag) : '')
                        }
                        placeholder="0"
                        onChange={(e) => setAuftragFeld(betragFormatieren(e.target.value))}
                        onBlur={() => {
                          if (auftragFeld === null) return;
                          wertMutation.mutate(betragLesen(auftragFeld));
                          setAuftragFeld(null);
                        }}
                      />
                      <span>€</span>
                    </span>
                  </label>

                  {a.status === 'Auftrag erteilt' && (
                    <button
                      type="button"
                      className={`detail-abgerechnet ${a.abgerechnet ? 'an' : ''}`}
                      onClick={() => abgerechnetMutation.mutate(!a.abgerechnet)}
                    >
                      {a.abgerechnet ? 'Abgerechnet ✓' : 'Als abgerechnet markieren'}
                    </button>
                  )}
                </div>
                <div
                  className="detail-auftrag-hinweis"
                  style={{ color: wertWarnung ? 'var(--eh-rot)' : 'var(--eh-text-5)' }}
                >
                  {auftragHinweis}
                </div>
              </div>
            </div>
          </div>

          {/* ---- Mail-Verlauf ---- */}
          <div className="detail-karte">
            <div className="detail-karte-kopf">
              <div className="detail-label">Mail-Verlauf</div>
              <div className="detail-anzahl">
                {a.erinnerungen.length === 1 ? '1 Mail' : `${a.erinnerungen.length} Mails`}
              </div>
            </div>
            <div className="detail-mailliste">
              {a.erinnerungen.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  className="detail-mailzeile"
                  onClick={() => setMailOffen(true)}
                >
                  <span className="detail-mail-art">{e.stufe}. Erinnerung</span>
                  <span className="detail-mail-zeit">
                    {e.wochentag}, {e.datumText} · {e.zeit || '—'}
                  </span>
                  <span className="detail-mail-user">{e.user}</span>
                  <span className="detail-mail-status">
                    <span className={`detail-mail-badge ${e.art === 'auto' ? 'gruen' : ''}`}>
                      {e.trockenlauf ? 'Entwurf im Testbetrieb' : e.artText}
                    </span>
                    <span className="detail-mail-chevron">›</span>
                  </span>
                </button>
              ))}
              {a.erinnerungen.length === 0 && (
                <div className="detail-leer">
                  {a.nachfass.mailVerfallen
                    ? 'Keine Erinnerungsmail versendet – dafür ist es inzwischen zu spät. ' +
                      'Kora holt sie nicht nach, weil eine automatische Nachfrage so lange nach ' +
                      'dem Angebot gedankenlos wirkt. Stattdessen steht der Anruf an.'
                    : 'Noch keine Erinnerungsmail zu diesem Angebot versendet.'}
                </div>
              )}
            </div>
          </div>

          {/* ---- Gesprächsnotizen ---- */}
          <div className="detail-karte">
            <div className="detail-karte-kopf">
              <div className="detail-label">Gesprächsnotizen</div>
              <div className="detail-anzahl">
                {a.anrufe.length === 1 ? '1 Anruf' : `${a.anrufe.length} Anrufe`}
              </div>
            </div>
            <div className="detail-anrufliste">
              {a.anrufe.map((c) => (
                <div
                  key={c.id}
                  className="detail-anruf"
                  style={{ borderLeftColor: c.farbe.akzent }}
                >
                  <div className="detail-anruf-kopf">
                    <span className="detail-anruf-zeit">
                      {c.wochentag}, {c.datumText} · {c.zeit}
                    </span>
                    <span className="detail-anruf-user">{c.user}</span>
                  </div>
                  <div className="detail-anruf-notiz">{c.notiz}</div>
                  <div className="detail-anruf-fuss">
                    <span
                      className="detail-anruf-badge"
                      style={{ background: c.farbe.bg, color: c.farbe.fg }}
                    >
                      {c.ergebnisLabel}
                    </span>
                    <span className="detail-anruf-zusatz">
                      {c.wiedervorlage ? `Wiedervorlage am ${dat(c.wiedervorlage)}` : c.sonst}
                    </span>
                  </div>
                </div>
              ))}
              {a.anrufe.length === 0 && (
                <div className="detail-leer">Noch kein Anruf dokumentiert.</div>
              )}
            </div>
          </div>

          {/* ---- Statusverlauf ---- */}
          <div className="detail-karte">
            <div className="detail-label" style={{ marginBottom: 18 }}>
              Statusverlauf
            </div>
            <div>
              {verlauf.map((h, i) => (
                <div className="detail-verlauf-zeile" key={`${h.datum}-${i}`}>
                  <div className="detail-verlauf-spur">
                    <span className="detail-verlauf-punkt" style={{ background: h.punkt }} />
                    <span
                      className="detail-verlauf-linie"
                      style={{ background: h.linie ? 'var(--eh-rahmen)' : 'transparent' }}
                    />
                  </div>
                  <div className="detail-verlauf-inhalt">
                    <div className="detail-verlauf-kopf">
                      <span className="detail-verlauf-status">{h.status}</span>
                      <span className="detail-verlauf-datum">{h.datumText}</span>
                      <span className="detail-verlauf-dauer">{h.dauerText}</span>
                    </div>
                    <div className="detail-verlauf-notiz">{h.notiz || '—'}</div>
                    <div className="detail-verlauf-user">
                      {h.user}
                      {h.aktuell ? ' · aktuell' : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- Rechte Spalte ---- */}
        <div className="detail-seitenspalte">
          <div className="detail-karte detail-datensatz">
            <div className="detail-label">Datensatz</div>
            {datensatz.map((m) => (
              <div className="detail-datenzeile" key={m.label}>
                <span className="detail-datenlabel">{m.label}</span>
                <span className="detail-datenwert">{m.wert}</span>
              </div>
            ))}
          </div>

          <div className="detail-karte detail-geschwister">
            <div className="detail-label" style={{ marginBottom: 13 }}>
              Weitere Angebote des Kunden
            </div>
            <div className="detail-geschwister-liste">
              {geschwister.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className="detail-geschwister-knopf"
                  onClick={() => navigate(`/angebot/${g.id}`)}
                >
                  <span className="detail-geschwister-name">{g.name}</span>
                  <span className="detail-geschwister-meta">
                    {g.status} · {g.alterText}
                  </span>
                </button>
              ))}
              {geschwister.length === 0 && (
                <div className="detail-leer">Einzelangebot — keine weiteren Vorgänge.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {gewerkOffen && (
        <GewerkModal
          angebot={a}
          onSchliessen={() => setGewerkOffen(false)}
          onFertig={(neueId) => {
            aktualisieren();
            navigate(`/angebot/${neueId}`);
          }}
        />
      )}
      {versendetOffen && (
        <VersendetModal
          angebot={a}
          onSchliessen={() => setVersendetOffen(false)}
          onFertig={aktualisieren}
        />
      )}
      {auftragOffen && (
        <AuftragModal
          angebot={a}
          onSchliessen={() => setAuftragOffen(false)}
          onFertig={aktualisieren}
        />
      )}
      {anrufOffen && (
        <AnrufModal angebot={a} onSchliessen={() => setAnrufOffen(false)} onFertig={aktualisieren} />
      )}
      {mailOffen && <MailVorschauModal angebot={a} onSchliessen={() => setMailOffen(false)} />}
    </div>
  );
}

function VerknuepfungsKarte({
  label,
  text,
  url,
  leerHinweis,
}: {
  label: string;
  text: string;
  url: string | null;
  leerHinweis: string;
}) {
  if (!url) {
    return (
      <div className="detail-verknuepfung detail-verknuepfung--leer">
        <span className="detail-verknuepfung-label">{label}</span>
        <span className="detail-verknuepfung-text">{leerHinweis}</span>
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="detail-verknuepfung">
      <span className="detail-verknuepfung-label">{label}</span>
      <span className="detail-verknuepfung-text">{text}</span>
    </a>
  );
}
